import { useAuth } from "@/components/auth/auth-context";
import { useToast } from "@/components/ui/use-toast";
import {
  useApproveStoryReview,
  useRequestStoryChanges,
  useStoryReview,
  type StoryReviewStatus,
} from "@/lib/api/mentor-story-review";
import { useUser } from "@/lib/api/user";
import {
  STORY_KEYS,
  STORY_META,
  STORY_STAGE,
  filledPhotoCount,
  type StoryKey,
} from "@/lib/mentor-stories/story-content";
import { PostHogEvents, useTrackEvent, useTrackPage } from "@/lib/posthog-events";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { getPreferredName } from "@/lib/redacted-display";
import { cn } from "@/lib/utils";
import { Button, Skeleton } from "@somethings-ui/web";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Check, Pencil, Plus } from "lucide-react";
import { useState } from "react";

const PREVIEW_WIDTH = 252;
const PREVIEW_SCALE = PREVIEW_WIDTH / STORY_STAGE.width;

function SubmittedSlide({ url }: { url?: string }) {
  const signed = useSignedMedia(url);
  return (
    <div
      className="w-full min-w-0 overflow-hidden rounded-2xl border border-surfaces-z5 bg-surfaces-z3"
      style={{
        height: STORY_STAGE.height * PREVIEW_SCALE,
        maxWidth: PREVIEW_WIDTH,
      }}
    >
      {signed.url ? (
        <img src={signed.url} alt="" className="size-full object-cover" />
      ) : null}
    </div>
  );
}

interface MentorStoryReviewScreenProps {
  reviewId: string;
}

const statusLabel = (status: StoryReviewStatus): string => {
  switch (status) {
    case "in_review":
      return "Story review · submitted for review";
    case "changes_requested":
      return "Story review · changes requested";
    case "approved":
      return "Story review · approved";
    case "in_progress":
      return "Story review · in progress";
    default: {
      const exhaustive: never = status;
      return exhaustive;
    }
  }
};

export function MentorStoryReviewScreen({
  reviewId,
}: MentorStoryReviewScreenProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user: actor } = useAuth();
  const { track } = useTrackEvent();
  const { data: review, isPending, isError } = useStoryReview(reviewId);
  const { data: mentor } = useUser(review?.userId ?? "");
  const approve = useApproveStoryReview();
  const requestChanges = useRequestStoryChanges();
  const [notes, setNotes] = useState<Partial<Record<StoryKey, string>>>({});
  const name = (mentor ? getPreferredName(mentor) : "") || "this mentor";
  const written = STORY_KEYS.flatMap((key) => {
    const note = notes[key]?.trim();
    return note ? [{ note, section: key }] : [];
  });
  const hasNotes = written.length > 0;
  const busy = approve.isPending || requestChanges.isPending;

  useTrackPage(
    PostHogEvents.ADMIN_STORY_REVIEW_OPENED,
    {
      admin_role: actor?.roleNames?.[0],
      lead_mentor_id: actor?.id,
      mentor_id: review?.userId,
    },
    { enabled: Boolean(review) },
  );

  const finish = (title: string) => {
    toast({ title });
    void navigate({ to: "/users" });
  };

  const handleApprove = async () => {
    if (hasNotes) return;
    try {
      await approve.mutateAsync(reviewId);
      track(PostHogEvents.ADMIN_STORY_APPROVED_AND_PUBLISHED, {
        admin_role: actor?.roleNames?.[0],
        lead_mentor_id: actor?.id,
        mentor_id: review?.userId,
      });
      finish(`${name}'s story has been approved and published.`);
    } catch {
      toast({ title: "Could not approve the story", variant: "destructive" });
    }
  };

  const handleReject = async () => {
    if (!hasNotes) return;
    try {
      await requestChanges.mutateAsync({ comments: written, reviewId });
      track(PostHogEvents.ADMIN_STORY_REJECTED_WITH_COMMENTS, {
        admin_role: actor?.roleNames?.[0],
        lead_mentor_id: actor?.id,
        mentor_id: review?.userId,
        stories_with_comments_count: written.length,
      });
      finish(`${name}'s story has been rejected with comments.`);
    } catch {
      toast({ title: "Could not send the comments", variant: "destructive" });
    }
  };

  const clearNote = (key: StoryKey) => {
    setNotes((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  return (
    <div className="m-xl rounded-2xl bg-surfaces-z3 px-2xl pb-3xl pt-2xl shadow-small">
      <div className="flex w-full max-w-[916px] flex-col gap-2xl">
        <Link
          to="/users"
          className="inline-flex items-center gap-md font-body text-body-1 font-bold text-text-primary"
        >
          <ArrowLeft className="size-5" />
          Back to Users
        </Link>
        {isPending ? <ReviewSkeleton /> : null}
        {isError || (!isPending && !review) ? (
          <p className="font-body text-body-2 text-text-primary">
            This story review is not available.
          </p>
        ) : null}
        {review ? (
          <>
          <div>
            <p className="font-body text-body-1 italic text-text-secondary">
              {statusLabel(review.status)}
            </p>
            <h1 className="mt-xs font-heading text-header-2 text-text-primary">
              Review {name}&apos;s stories
            </h1>
            <p className="mt-sm max-w-[640px] font-body text-body-1 text-text-secondary">
              Check what teens will tap through after matching. Flag anything that
              needs a change — each note goes back to {name} tied to that story.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-xl">
            {STORY_KEYS.map((key) => {
              const meta = STORY_META[key];
              const section = review.draft[key];
              const count = filledPhotoCount(section, key);
              return (
                <section
                  key={key}
                  className={cn(
                    "flex min-w-0 flex-col gap-xl self-stretch rounded-2xl bg-surfaces-z0 p-xl shadow-low",
                    notes[key] !== undefined
                      ? "ring-2 ring-inset ring-text-primary"
                      : "ring-1 ring-inset ring-surfaces-divider",
                  )}
                >
                  <div className="flex items-start justify-between gap-md">
                    <p className="font-mono text-body-4 uppercase text-text-tertiary">
                      {meta.eyebrow}
                    </p>
                    <p className="font-body text-body-2 text-text-secondary">
                      {count} {count === 1 ? "photo" : "photos"}
                    </p>
                  </div>
                  <SubmittedSlide
                    url={
                      review.draft.slides.find(
                        (slide) => slide.order === STORY_KEYS.indexOf(key),
                      )?.url
                    }
                  />
                  {notes[key] !== undefined ? (
                    <div className="mt-auto flex flex-col gap-sm">
                      <div className="flex items-center justify-between">
                        <p className="inline-flex items-center gap-sm font-body text-body-1 text-text-tertiary">
                          <Pencil className="size-4" />
                          Change requested
                        </p>
                        <Button
                          size="sm"
                          variant="quaternary"
                          fullWidth={false}
                          className="h-auto px-0"
                          onPress={() => clearNote(key)}
                        >
                          Remove
                        </Button>
                      </div>
                      <textarea
                        className="min-h-[92px] w-full resize-none rounded-xl border border-surfaces-z5 bg-surfaces-z0 px-lg py-md font-body text-body-1 text-text-primary outline-none placeholder:text-text-tertiary"
                        placeholder={`Write a note for ${name}`}
                        value={notes[key] ?? ""}
                        onChange={(event) =>
                          setNotes((current) => ({
                            ...current,
                            [key]: event.target.value,
                          }))
                        }
                      />
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="tertiary"
                      className="mt-auto"
                      leadingIcon={<Plus className="size-5" strokeWidth={1.75} />}
                      onPress={() =>
                        setNotes((current) =>
                          current[key] !== undefined ? current : { ...current, [key]: "" },
                        )
                      }
                    >
                      Request a change
                    </Button>
                  )}
                </section>
              );
            })}
          </div>
          <div className="h-px w-full bg-surfaces-z5" />
          <div className="flex justify-end pt-sm">
            <div className="flex items-center gap-xl">
              {hasNotes ? (
                <Button
                  size="lg"
                  variant="secondary"
                  fullWidth={false}
                  disabled={busy}
                  onPress={() => setNotes({})}
                >
                  Clear all changes
                </Button>
              ) : (
                <Button size="lg" variant="secondary" fullWidth={false} disabled>
                  Reject with comments
                </Button>
              )}
              {hasNotes ? (
                <Button
                  size="lg"
                  fullWidth={false}
                  disabled={busy}
                  loading={requestChanges.isPending}
                  onPress={() => void handleReject()}
                >
                  Reject with comments
                </Button>
              ) : (
                <Button
                  size="lg"
                  fullWidth={false}
                  disabled={busy}
                  loading={approve.isPending}
                  leadingIcon={
                    approve.isPending ? undefined : (
                      <Check className="size-6" strokeWidth={2.5} />
                    )
                  }
                  onPress={() => void handleApprove()}
                >
                  Approve and publish
                </Button>
              )}
            </div>
          </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function ReviewSkeleton() {
  return (
    <div className="flex flex-col gap-2xl">
      <div>
        <Skeleton className="h-4 w-64 rounded-full" />
        <Skeleton className="mt-md h-9 w-96 max-w-full rounded-full" />
        <Skeleton className="mt-md h-4 w-[520px] max-w-full rounded-full" />
      </div>
      <div className="grid grid-cols-3 gap-xl">
        {STORY_KEYS.map((key) => (
          <Skeleton key={key} className="h-[568px] min-w-0 rounded-2xl" />
        ))}
      </div>
    </div>
  );
}
